import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const IDTA_BLUE = '#0028CD';

const services = [
  {
    title: 'Discovery',
    description:
      'Find business partners and directly discover their AAS endpoints through UI and API.',
    features: [
      'Discover business partners and their AAS endpoints',
      'Integrated AAS client for direct search and discovery from data providers',
      'Available as an API for service integration',
    ],
    tag: 'Discovery',
    featured: true,
    image: '/static/css/assets/img/discovery.png',
    link: '/dashboard',
  },
  {
    title: 'Identity Service',
    description:
      'Central identity service for secure access to all ADX Hub services.',
    features: [
      'Central IDP for authentication',
      'Supports X.509 (x5c) certificate-based authentication and Microsoft Entra ID client-credentials',
      'Provides federated F-X tokens via token exchange aligned with M-X (Port Leo) to requet data from data providers',
      'Maintains a trustlist to validate trusted consumers and exchanged tokens',
    ],
    tag: 'Identity & Trust',
    image: '/static/css/assets/img/icon_blue_1_identity_provider.png',
    link: '/dashboard',
  },
  {
    title: 'Validation Service',
    description:
      'Conformance checks for AAS Submodels, files, and APIs against IDTA specifications.',
    features: [
      'Validates AAS files, Submodels, and APIs',
      'Supports API profile validation based on IDTA specifications',
      'Available as an API for service integration',
    ],
    tag: 'Conformance',
    image: '/static/css/assets/img/icon_blue_3_aas_validation_service.png',
    link: '/dashboard',
  },
  {
    title: 'SMT Repository',
    description:
      'Repository for IDTA Submodel templates across versions, with API and UI access.',
    features: [
      'Fetch IDTA Submodel templates via APIs across all versions',
      'Advanced UI search for Submodel templates',
      'Download available in AASX and JSON formats',
      'Specification PDFs available for template reference',
    ],
    tag: 'Templates',
    image: '/static/css/assets/img/icon_blue_3_submodel_template_repository.png',
    link: '/dashboard',
  },
  {
    title: 'AAS Editor',
    description:
      'Guided workspace for creating and managing Asset Administration Shells.',
    features: [
      'Easy AAS onboarding',
      'Guided use case creation',
      'Create, edit, and deploy AAS',
    ],
    tag: 'Modelling',
    image: '/static/css/assets/img/editor.png',
    link: '/dashboard',
  },
];

function ServiceCard({ service, index }) {
  const featured = service.featured;

  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      data-testid={`service-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
      className={`group relative flex flex-col rounded-2xl border border-[#0028CD] bg-white p-6 transition-colors duration-300 hover:border-[#FF500F] md:p-7 ${
        featured ? 'xl:col-span-2' : ''
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[rgba(0,40,205,0.06)] sm:h-16 sm:w-16">
          <img
            src={service.image}
            alt={service.title}
            className="h-9 w-9 object-contain sm:h-10 sm:w-10"
          />
        </div>
        <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
          {service.tag}
        </span>
      </div>

      <h3
        className={`mt-5 font-semibold leading-tight tracking-tight text-[#0028CD] ${
          featured ? 'text-[22px] md:text-[24px]' : 'text-[18px]'
        }`}
      >
        {service.title}
      </h3>
      {service.description ? (
        <p
          className={`mt-2 leading-[1.6] text-slate-500 ${
            featured ? 'text-[15px] md:max-w-xl' : 'text-[14px]'
          }`}
        >
          {service.description}
        </p>
      ) : null}

      <div
        className={`h-px w-full bg-slate-100 ${service.description ? 'mt-5' : 'mt-4'}`}
      />

      <ul
        className={`mt-5 space-y-2.5 ${
          featured ? 'sm:grid sm:grid-cols-2 sm:gap-x-8 sm:gap-y-2.5 sm:space-y-0' : ''
        }`}
      >
        {service.features.map((feature) => (
          <li
            key={feature}
            className="flex items-start gap-2.5 text-[13.5px] leading-[1.5] text-slate-600"
          >
            <span
              aria-hidden
              className="mt-[7px] h-[5px] w-[5px] shrink-0 rounded-full transition-colors duration-300 group-hover:bg-[#FF500F]"
              style={{ backgroundColor: 'rgba(0,40,205,0.5)' }}
            />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto pt-7">
        <a
          href={service.link}
          className="inline-flex cursor-pointer items-center gap-1.5 rounded-xl border-2 border-[#0028CD] bg-white px-5 py-2 text-xs font-bold text-[#0028CD] transition-colors hover:border-[#FF500F]"
        >
          Open Service
          <ArrowRight
            size={14}
            className="transition-transform duration-300 group-hover:translate-x-0.5"
          />
        </a>
      </div>
    </motion.article>
  );
}

const Services = () => {
  return (
    <section
      id="services"
      className="relative overflow-hidden bg-slate-50/40 pt-10 pb-12 md:pt-20 md:pb-20"
      data-testid="services-section"
    >
      <div className="container relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.45 }}
          className="mx-auto max-w-2xl text-center"
        >
          <h2 className="text-3xl font-bold tracking-tight text-[#0028CD] md:text-4xl">
            Services
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-[15px] leading-relaxed text-slate-500">
            Identity, Discovery, Validation, Submodel Templates, and Editor for AAS-based data exchange.
          </p>
        </motion.div>

        <div className="mt-10 grid grid-cols-1 gap-4 md:mt-14 md:grid-cols-2 md:gap-5 xl:grid-cols-3">
          {services.map((service, index) => (
            <ServiceCard key={service.title} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
